import { useMemo, useCallback } from "react"
import { chain, filter } from "lodash"
import moment from "moment"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createExposureMultiDateQuery } from "gatherers/multi-date/exposure"
import { isIntradaySimulationEligible } from "gatherers/helpers"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { getFactorLabel } from "helpers/factors"
import { numberFormatter } from "helpers/formatters/numbers"
import { ModelMetadata } from "resources/models"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_EXPOSURE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"

interface ExposureItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: "factor"
}

interface ExposureDataPoint {
  date: moment.Moment
  [key: string]: unknown
}

function createExposureItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean | undefined>,
): ExposureItem[] {
  if (!modelMetadata?.factors) return []

  const factorsByIdMap = chain(modelMetadata.factors).keyBy("id").value()

  const factors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: getFactorLabel(factorsByIdMap, factor.id),
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor" as const,
  }))
}

interface ExposureGraphProps {
  modelMetadata: ModelMetadata
}

export default function ExposureGraph({ modelMetadata }: ExposureGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_EXPOSURE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ isIntradayMode, lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !focusedItemId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.exposure",
      title: "Asset View: Exposure",
      view: "exposure",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: clickPosition?.date ?? endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: focusedItemId,
        selectedCategory,
        clickedDate: clickPosition?.date,
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter ?? undefined, normalization)
  const segment = useFilteredSegment(portfolioFilter ?? undefined, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors()
  const currentItems = useMemo(
    () => createExposureItems(modelMetadata, selectedCategory, disabledItems),
    [modelMetadata, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: ExposureItem[], data: ExposureDataPoint[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => [
    createExposureMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    ),
  ], [modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, lastIntradayBatchId)

  const isOneDayMode = isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId)
  const noDataMessage = isOneDayMode ? "Intraday exposure data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      formatter: numberFormatter,
      loadingMessage: "Loading exposure data...",
      errorMessage: "Failed to load exposure data. Please try again.",
      emptyMessage: "No exposure data available.",
      noDataMessage,
      isOneDayMode,
    }}
  />
}
